import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Pause, Play } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Eyebrow } from "../components/Reveal";
import { Pictogram } from "../components/brand/Pictogram";
import { CtaBand } from "../components/CtaBand";
import { SOLUTIONS, themeBySlug } from "../data/content";

const DELAY = 4000;

const Slide = ({ s }) => {
  const themes = s.themes.map(themeBySlug);
  const c = themes[0].color;
  return (
    <div className="grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-6">
        <Eyebrow color={c}>Ce que vous obtenez</Eyebrow>
        <h2 className="mt-5 font-display text-3xl lg:text-4xl font-extrabold" data-testid="solution-slide-title">{s.title}</h2>
        <p className="mt-4 text-ink/65 text-lg">{s.lead}</p>
        <ul className="mt-8 space-y-4" data-testid="solution-points">
          {s.points.map((p) => (
            <li key={p} className="flex gap-4 text-lg"><Check className="shrink-0 mt-1" style={{ color: c }} />{p}</li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-6 space-y-4">
        <Eyebrow>Métiers mobilisés</Eyebrow>
        {themes.map((t) => (
          <Link key={t.slug} to={`/services/${t.slug}`} data-testid={`solution-theme-${t.slug}`} className="group flex items-center gap-5 bg-white border border-ink/10 p-6 hover:shadow-lg transition-shadow" style={{ borderLeft: `5px solid ${t.color}` }}>
            <Pictogram name={t.icon} color={t.color} size={44} />
            <span className="flex-1"><span className="font-display text-xl font-bold block">{t.name}</span><span className="text-sm text-ink/60">{t.short}</span></span>
            <ArrowRight className="group-hover:translate-x-1 transition-transform" />
          </Link>
        ))}
      </div>
    </div>
  );
};

export default function Solution() {
  const { slug } = useParams();
  const start = Math.max(0, SOLUTIONS.findIndex((x) => x.slug === slug));
  const [i, setI] = useState(start);
  const [paused, setPaused] = useState(false);
  const s = SOLUTIONS[i];
  const first = themeBySlug(s.themes[0]);
  const go = (n) => setI((n + SOLUTIONS.length) % SOLUTIONS.length);

  useEffect(() => {
    setI(start);
  }, [start]);
  useEffect(() => {
    if (paused) return;
    const id = setTimeout(() => go(i + 1), DELAY);
    return () => clearTimeout(id);
  }, [i, paused]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="solution-page">
      <PageHero eyebrow="Solutions par besoin" title="Partez de votre besoin, on assemble les métiers." lead="Des situations que vivent nos clients, et la façon dont ENOVA y répond." color={first.color}>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="solution-tabs">
          {SOLUTIONS.map((x, k) => {
            const c = themeBySlug(x.themes[0]).color;
            return (
              <button key={x.slug} onClick={() => go(k)} data-testid={`solution-tab-${x.slug}`} className={`text-left px-4 pt-3 pb-4 border transition-colors ${k === i ? "bg-white text-ink border-white" : "border-white/25 text-white/75 hover:border-white"}`}>
                <span className="font-display font-bold">{x.title}</span>
                <span className="mt-3 block h-1 bg-current/10 overflow-hidden" style={{ background: k === i ? "rgba(51,51,51,.12)" : "rgba(255,255,255,.15)" }}>
                  {k === i && (
                    <motion.span key={`${i}-${paused}`} className="block h-full" style={{ background: c }} initial={{ width: paused ? "100%" : "0%" }} animate={{ width: "100%" }} transition={{ duration: paused ? 0 : DELAY / 1000, ease: "linear" }} />
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </PageHero>
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-24" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="solution-carousel">
        <div className="flex items-center justify-end gap-2 mb-8">
          <span className="mr-auto text-sm text-ink/50" data-testid="solution-counter">{i + 1} / {SOLUTIONS.length}</span>
          <button onClick={() => setPaused(!paused)} data-testid="solution-pause-btn" className="h-10 w-10 grid place-items-center border border-ink/20 hover:bg-white" aria-label={paused ? "Lecture" : "Pause"}>{paused ? <Play size={16} /> : <Pause size={16} />}</button>
          <button onClick={() => go(i - 1)} data-testid="solution-prev-btn" className="h-10 w-10 grid place-items-center border border-ink/20 hover:bg-white" aria-label="Précédent"><ArrowLeft size={16} /></button>
          <button onClick={() => go(i + 1)} data-testid="solution-next-btn" className="h-10 w-10 grid place-items-center border border-ink/20 hover:bg-white" aria-label="Suivant"><ArrowRight size={16} /></button>
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={s.slug} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
            <Slide s={s} />
          </motion.div>
        </AnimatePresence>
      </section>
      <CtaBand color={first.color} service={first.slug} title={`${s.title} : parlons-en.`} />
    </div>
  );
}
